import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { ExtensionsType } from '../../api/types'
import { Installation } from '../../installations/models'
import { TFunction } from 'i18next'
import { PermissionType } from 'src/Libs/types/common'
import { ApiPermissionType } from 'src/Libs/api/types'
import { APIAccessGroups } from 'src/Libs/feature-flags/types'

/**
 * Returns the default display name used for a AccountLink
 *
 * If the given model is a company, the company name is used.
 * If it is not a company the return value is constructed out of the available first and last name.
 *
 * @param item
 */
export const getDisplayName = (item: AccountLinkModel): string => {
  if (item.permissionType === ExtensionsType.luoxEnergy) return item.userName
  if (item.accountType === 'user') {
    return [item.firstName, item.lastName].join(' ')
  }
  return item.companyName
}

/**
 * return session storage key to track if dialog for access requests
 * of the installation is shown
 */
export const getOwnerAccessRequestSessionKey = (installationId: number): string => {
  return `is-owner-access-request-dialog-shown-${installationId}`
}

/**
 *  return query key used to store access request by react-query
 */
export const getOwnerAccessRequestQueryKey = (installationId: number): string[] => {
  return ['installations', 'owner-access-requests', `${installationId}`]
}

/** check if account link is of the installation owner */
export const isInstallationOwner = (item: AccountLinkModel, installation: Installation): boolean => {
  return installation.ownerAccountId === item.accountId.toString()
}

/**
 * Check if an account link may get the Hager Energy API access, based on the feature-flag groups.
 * The installation owner is always allowed, independent of the user group.
 */
export const isAccountAllowedToGetApiAccess = (
  accountLink: AccountLinkModel,
  installation: Installation,
  apiAccessGroups: APIAccessGroups | undefined
): boolean => {
  const isUserAndUserGroupEnabled =
    (accountLink.accountType === 'user' && (apiAccessGroups?.user ?? false)) || isInstallationOwner(accountLink, installation)
  const isCompanyAndCompanyGroupEnabled = accountLink.accountType === 'company' && (apiAccessGroups?.company ?? false)

  return isUserAndUserGroupEnabled || isCompanyAndCompanyGroupEnabled
}

export const isDirektMarketingAccount = (item: AccountLinkModel): boolean => {
  return item.permissionType === ExtensionsType.luoxEnergy && item.storagePermissionType === ExtensionsType.luoxEnergy
}

/** return translation for account link status  */
export const getAccountLinkStatus = (permissionType: PermissionType, t: TFunction): string => {
  if (permissionType === ApiPermissionType.blocked) {
    return t('installation.access-and-invitations.table.columns.blocked')
  }

  if (permissionType === ApiPermissionType.pending) {
    return t('installation.access-and-invitations.table.columns.pending')
  }

  return t('installation.access-and-invitations.table.columns.approved')
}
